import { createMemo, Show, type Accessor } from 'solid-js'
import coinIcon from '~/assets/currency/coin.svg'
import gemIcon from '~/assets/currency/gem.svg'
import type { CatalogueCurrency, CataloguePrice } from '~/lib/catalogue-price'
import {
  resolveEffectivePrice,
  type ProductPricing,
} from '~/lib/effective-price'
import { intlLocale } from '~/lib/params'
import { m } from '~/paraglide/messages'

const numberFormatter = new Intl.NumberFormat(intlLocale)

// Twin number-valued memos over the effective price, shared by every price
// surface: a clock tick only touches the DOM when the price actually flips
// across a sale-window edge.
export function createEffectivePriceParts(
  pricing: () => ProductPricing,
  serverNow: Accessor<number>,
): { amount: Accessor<number>; original: Accessor<number | null> } {
  const amount = createMemo(
    () => resolveEffectivePrice(pricing(), serverNow()).amount,
  )
  const original = createMemo(
    () => resolveEffectivePrice(pricing(), serverNow()).original,
  )
  return { amount, original }
}

// A paid catalogue price as of server now: its currency and the two
// effective-price parts.
export interface PaidPriceParts {
  amount: number
  currency: CatalogueCurrency
  original: number | null
}

const samePaidParts = (
  left: PaidPriceParts | null,
  right: PaidPriceParts | null,
) =>
  left === right ||
  (left !== null &&
    right !== null &&
    left.amount === right.amount &&
    left.currency === right.currency &&
    left.original === right.original)

// createEffectivePriceParts for a catalogue price — card, hero, and detail
// alike: null for a free product, which has no effective price (GLOSSARY.md
// "Free product"). Like the twin memos, a clock tick only notifies when the
// parts change.
export function createCataloguePriceParts(
  price: () => CataloguePrice,
  serverNow: Accessor<number>,
): Accessor<PaidPriceParts | null> {
  const parts = createMemo<PaidPriceParts | null>(
    () => {
      const current = price()
      if (current.kind === 'free') return null
      const { amount, original } = resolveEffectivePrice(
        current.pricing,
        serverNow(),
      )
      return { amount, currency: current.currency, original }
    },
    null,
    { equals: samePaidParts },
  )
  return parts
}

// What every price surface shows for a free product in place of a currency
// icon and number. The copy is the Plus row's try CTA, `btn_try_plus`, whose
// every translation is the bare adjective (GLOSSARY.md "Free product").
export const freePriceLabel = () => m.btn_try_plus()

// The struck-through original beside an active sale's effective price
// (GLOSSARY.md "Effective price"); renders nothing outside the window.
export function StruckOriginalPrice(props: {
  class?: string
  original: number | null
  testId?: string
}) {
  return (
    <Show when={props.original !== null}>
      <s
        data-testid={props.testId ?? 'price-original'}
        class={props.class ?? 'font-normal text-[#9BB1AB] line-through'}
      >
        {numberFormatter.format(props.original!)}
      </s>
    </Show>
  )
}

// The price on a purchase confirmation's CTA (GLOSSARY.md "Shop purchase
// flow"): the currency icon at 16px, the struck original during a sale, and
// the effective price, 4px apart on the inverted button.
export function ConfirmPriceCta(props: {
  amount: number
  currency: CatalogueCurrency
  original: number | null
}) {
  return (
    <span class="flex items-center gap-1">
      <img
        src={props.currency === 'coin' ? coinIcon : gemIcon}
        alt=""
        class="size-4"
      />
      <StruckOriginalPrice
        class="font-normal text-white/60 line-through"
        original={props.original}
      />
      {numberFormatter.format(props.amount)}
    </span>
  )
}
